import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "@/components/admin/Ui";
import { User, EnvelopeSimple, CalendarBlank, Bell, Lock, WhatsappLogo } from "@phosphor-icons/react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [wa, setWa] = useState(null);
  const [busy, setBusy] = useState(false);
  const [waBusy, setWaBusy] = useState(false);

  const load = async () => {
    const [{ data }, { data: waData }] = await Promise.all([
      api.get("/settings"),
      api.get("/whatsapp/config"),
    ]);
    setS(data);
    setWa(waData);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/settings", s);
      toast.success("Settings saved");
      await load();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const saveWa = async () => {
    setWaBusy(true);
    try {
      await api.patch("/whatsapp/config", wa);
      toast.success("WhatsApp config saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setWaBusy(false); }
  };

  if (!s || !wa) return <div className="p-8 text-navy/60">Loading…</div>;

  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="Operator settings" subtitle="Profile · Outreach defaults · Alerts" action={
        <button onClick={save} disabled={busy} data-testid="settings-save-button" className="btn-primary">{busy ? "Saving…" : "Save settings"}</button>
      } />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Operator profile" icon={User}>
          <Field label="Operator name">
            <input data-testid="settings-name" value={s.operator_name} onChange={(e) => setS({ ...s, operator_name: e.target.value })} className={input} />
          </Field>
          <Field label="Operator email">
            <input data-testid="settings-email" value={s.operator_email} onChange={(e) => setS({ ...s, operator_email: e.target.value })} className={input} />
          </Field>
        </Section>

        <Section title="Outreach defaults" icon={EnvelopeSimple}>
          <Field label="Send emails as (alias)">
            <input data-testid="settings-send-as" value={s.send_emails_as} onChange={(e) => setS({ ...s, send_emails_as: e.target.value })} className={input} placeholder="info@europacific.si" />
          </Field>
          <Field label="Default meeting link (Calendly / Bookings)">
            <input data-testid="settings-meeting-link" value={s.default_meeting_link} onChange={(e) => setS({ ...s, default_meeting_link: e.target.value })} className={input} placeholder="https://outlook.office.com/bookwithme/..." />
          </Field>
          <Field label="Email signature">
            <textarea data-testid="settings-signature" rows={5} value={s.email_signature} onChange={(e) => setS({ ...s, email_signature: e.target.value })} className={input} />
          </Field>
        </Section>

        <Section title="Meeting delegate" icon={CalendarBlank}>
          <p className="text-xs text-navy/60 mb-3">Optional — if you assign a colleague to take prospect calls.</p>
          <Field label="Delegate name">
            <input data-testid="settings-delegate-name" value={s.delegate_name} onChange={(e) => setS({ ...s, delegate_name: e.target.value })} className={input} />
          </Field>
          <Field label="Delegate email">
            <input data-testid="settings-delegate-email" value={s.delegate_email} onChange={(e) => setS({ ...s, delegate_email: e.target.value })} className={input} />
          </Field>
          <Field label="Delegate meeting link">
            <input data-testid="settings-delegate-link" value={s.delegate_meeting_link} onChange={(e) => setS({ ...s, delegate_meeting_link: e.target.value })} className={input} />
          </Field>
        </Section>

        <Section title="WhatsApp community" icon={WhatsappLogo}>
          <p className="text-xs text-navy/60 mb-3">
            Private members-only community with 2 rooms — Announcements (admin broadcast) and All Members (peer chat).
            Create it in WhatsApp → Communities → New community, then paste the invite link here.
          </p>
          <Field label="Community invite link">
            <input data-testid="settings-wa-link" value={wa.community_invite_link} onChange={(e) => setWa({ ...wa, community_invite_link: e.target.value })} className={input} placeholder="https://chat.whatsapp.com/..." />
          </Field>
          <Field label="Admin WhatsApp number (yours, E.164)">
            <input data-testid="settings-wa-admin-phone" value={wa.admin_whatsapp_number} onChange={(e) => setWa({ ...wa, admin_whatsapp_number: e.target.value })} className={input} placeholder="+38640793000" />
          </Field>
          <Field label="Display name in DM sign-off">
            <input data-testid="settings-wa-admin-name" value={wa.admin_display_name} onChange={(e) => setWa({ ...wa, admin_display_name: e.target.value })} className={input} placeholder="Rok Kobal — EPAS" />
          </Field>
          <div className="pt-2">
            <button onClick={saveWa} disabled={waBusy} data-testid="settings-wa-save" className="btn-primary">
              {waBusy ? "Saving…" : "Save WhatsApp config"}
            </button>
          </div>
        </Section>

        <Section title="Alerts (Pushover)" icon={Bell}>
          <p className="text-xs text-navy/60 mb-3">Optional — get a phone push when high-value events fire.</p>
          <Field label="Pushover user key">
            <input data-testid="settings-push-user" value={s.pushover_user_key} onChange={(e) => setS({ ...s, pushover_user_key: e.target.value })} className={input} placeholder="u_xxxxxx" />
          </Field>
          <Field label="Pushover app token">
            <input data-testid="settings-push-token" value={s.pushover_app_token === "***" ? "" : s.pushover_app_token} onChange={(e) => setS({ ...s, pushover_app_token: e.target.value })} className={input} placeholder="a_xxxxxx" />
            {s.pushover_app_token === "***" && <span className="text-[10px] text-navy/40 mt-1 inline-flex items-center gap-1"><Lock size={10} /> token stored (leave blank to keep)</span>}
          </Field>
          <div className="space-y-2 pt-2">
            <Check label="Alert on `interested` reply" v={s.alert_on_interested} onChange={(v) => setS({ ...s, alert_on_interested: v })} testId="settings-alert-interested" />
            <Check label="Alert on chatbot lead captured" v={s.alert_on_lead_captured} onChange={(v) => setS({ ...s, alert_on_lead_captured: v })} testId="settings-alert-lead" />
            <Check label="Alert on new application" v={s.alert_on_application} onChange={(v) => setS({ ...s, alert_on_application: v })} testId="settings-alert-app" />
          </div>
        </Section>
      </div>
    </div>
  );
}

const input = "w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy";

const Field = ({ label, children }) => (
  <div className="mb-3"><label className="overline mb-2 block">{label}</label>{children}</div>
);

const Check = ({ label, v, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm text-navy/80 cursor-pointer">
    <input type="checkbox" data-testid={testId} checked={!!v} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-black/10 p-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} weight="duotone" className="text-ember" />
      <div className="overline">{title}</div>
    </div>
    {children}
  </div>
);
